import { extendError, getConfiguration, verifyExtend } from "./extend-utils.js";
import { initializeCheckoutPlus } from "./extend-checkout-plus.js"

const cartOfferTag = 'extend-cart-offer';
let cart;
let init = false;

const {
    cartOfferEnabled,
    cartSPEnabled,
    sideCartOfferEnabled,
    cartBalancingEnabled,
    SPEnabled
} = getConfiguration();

// Determine cart type and selectors based on page location
const getCartConfig = () => {
    if (/cart/.test(window.location.pathname) && cartOfferEnabled) {
        return {
            type: 'main',
            selectors: {
                cartContainer: '#cart',
                cartRowItem: '[id^="CartDrawer-Item"]',
                cartRowItemImage: 'a',
                cartRowItemTitle: 'a.text-t-brand-primary',
                cartRowItemQuantity: 'input[name="updates[]"]',
                warrantyOriginalMeta: 'dl.text-utility',
                offerAnchor: 'dl.text-utility',
                metadataContainer: 'dl.text-utility',
                quantityWrapper: 'quantity-input',
                lineItemRemove: 'cart-remove-toggle',
                shippingAnchor: '.cart__footer'
            }
        };
    } else if (sideCartOfferEnabled) {
        return {
            type: 'side',
            selectors: {
                cartContainer: '#cart',
                cartRowItem: '[id^="CartDrawer-Item"]',
                cartRowItemImage: 'a',
                cartRowItemTitle: 'a.text-t-brand-primary',
                cartRowItemQuantity: 'input[name="updates[]"]',
                warrantyOriginalMeta: 'dl.text-utility',
                offerAnchor: 'dl.text-utility',
                metadataContainer: 'dl.text-utility',
                quantityWrapper: 'quantity-input',
                lineItemRemove: 'cart-remove-toggle',
                shippingAnchor: '.cart__footer'
            }
        };
    }
    return { type: null, selectors: null };
};

const { type, selectors } = getCartConfig();

// Create offer parameters
const createOfferParams = (referenceId, quantity, price, category) => ({
    referenceId,
    quantity,
    price,
    category
});

// Create custom element
const createCartOfferElement = (params) => {
    const element = document.createElement(cartOfferTag);
    element.params = params;
    return element;
};

// Render the Extend offer button
const renderExtendOffer = (element, params, onSuccess) => {
    Extend.buttons.renderSimpleOffer(element, {
        referenceId: params.referenceId,
        price: params.price,
        category: params.category,
        onAddToCart: ({ plan, product }) => {
            ExtendShopify.addPlanToCart({
                plan,
                product,
                quantity: params.quantity
            }, (err) => {
                if (err) {
                    throw new Error(err.message);
                }
                document.dispatchEvent(new Event('extend.lineItemModified'));
                if (onSuccess) onSuccess();
            });
        }
    });
};

// Custom element
const defineCartOfferElement = () => {
    if (customElements.get(cartOfferTag)) return;
    
    customElements.define(cartOfferTag, class extends HTMLElement {
        constructor() {
            super();
            this.params = null;
        }
        
        connectedCallback() {
            if (this.params) {
                renderExtendOffer(this, this.params);
            }
        }
    });
};

defineCartOfferElement();

// Check if warranty already exists in cart
const isWarrantyInCart = (referenceId, cartItems) => {
    return ExtendShopify.warrantyAlreadyInCart(referenceId, cartItems) || 
           ExtendShopify.warrantyAlreadyInCart(referenceId.toString(), cartItems);
};

// Pure function to check if offer params have changed
const haveParamsChanged = (existingOffer, newParams) => {
    if (!existingOffer || !existingOffer.params) return true;
    
    return existingOffer.params.referenceId !== newParams.referenceId ||
           existingOffer.params.quantity !== newParams.quantity ||
           existingOffer.params.price !== newParams.price ||
           existingOffer.params.category !== newParams.category;
};

// Remove existing offer if present
const removeExistingOffer = (lineItemElement) => {
    const existingOffer = lineItemElement.querySelector(cartOfferTag);
    if (existingOffer) {
        existingOffer.remove();
    }
    return existingOffer;
};

// Append offer to the appropriate location
const appendOffer = (anchor, offer, cartType) => {
    if (cartType === 'main') {
        anchor.append(offer);
    } else if (cartType === 'side') {
        anchor.before(offer);
    }
};

// Create warranty offer
const createWarrantyOffer = (lineItemElement, index) => {
    if (!cartOfferEnabled) return;
    
    const cartItem = cart.items[index];
    const params = createOfferParams(
        cartItem.id,
        cartItem.quantity,
        cartItem.price,
        cartItem.product_type
    );
    
    const warrantyExists = isWarrantyInCart(params.referenceId, cart.items);
    const anchor = lineItemElement.querySelector(selectors.offerAnchor);
    
    if (!anchor) {
        throw new Error(`Offer anchor element on line ${index} not found with selector ${selectors.offerAnchor}`);
    }
    
    const existingOffer = lineItemElement.querySelector(cartOfferTag);
    
    // If warranty is already in cart, remove offer and return
    if (warrantyExists) {
        removeExistingOffer(lineItemElement);
        return;
    }
    
    // If params have changed, remove old offer
    if (existingOffer && haveParamsChanged(existingOffer, params)) {
        removeExistingOffer(lineItemElement);
    }
    
    // Create and append new offer if it doesn't exist
    if (!lineItemElement.querySelector(cartOfferTag)) {
        const newOffer = createCartOfferElement(params);
        appendOffer(anchor, newOffer, type);
    }
};

// Pure function to disable pointer events on element
const disablePointerEvents = (element) => {
    if (element) {
        element.style.pointerEvents = 'none';
    }
};

// Pure function to apply opacity and disable pointer events
const applyDisabledStyle = (element) => {
    if (element) {
        element.style.opacity = '75%';
        element.style.pointerEvents = 'none';
    }
};

// Pure function to extract warranty metadata from cart item
const getWarrantyMetadata = (cartItem, cartItems) => {
    let productData, termData;
    
    if (cartItem.options_with_values && cartItem.options_with_values[1]) {
        const regexReplace = cartItem.options_with_values[0].value.match(/\-\d{5,}/g);
        productData = cartItem.options_with_values[0].value.replace(regexReplace, '');
        termData = cartItem.options_with_values[1].value;
    } else {
        productData = cartItem.properties.Product;
        termData = cartItem.properties.Term;
    }
    
    // Handle category offers
    if (productData === "Covered Product") {
        const coveredProductId = cartItem.properties['_Extend.ProductId'];
        const coveredProduct = cartItems.find((item) => item.id.toString() === coveredProductId);
        productData = coveredProduct ? coveredProduct.title : productData;
    }
    
    return { productData, termData };
};

// Pure function to create metadata element
const createMetadataElement = (label, value, className, cartType) => {
    const element = document.createElement('div');
    element.className = `${className} extend-warranty-info extend-warranty-info-${label.toLowerCase()}`;
    
    if (cartType === 'main') {
        element.innerHTML = `<dt>${label}: </dt> <dd>${value}</dd>`;
    } else if (cartType === 'side') {
        element.innerHTML = `<span class="qsc2-product-item__option-name"><strong>${label}:</strong></span> <span class="qsc2-product-item__option-value">${value}</span>`;
    }
    
    return element;
};

// Pure function to get metadata class name
const getMetadataClassName = (cartType) => {
    if (cartType === 'main') {
        return selectors.warrantyOriginalMeta.split(' ')[1].substring(1);
    }
    return selectors.warrantyOriginalMeta.substring(1);
};

// Function to append warranty metadata to container
const appendWarrantyMetadata = (container, productData, termData, cartType) => {
    if (container.querySelector('.extend-warranty-info')) return;
    
    const className = getMetadataClassName(cartType);
    const productElement = createMetadataElement('Product', productData, className, cartType);
    const termElement = createMetadataElement('Term', termData, className, cartType);
    
    container.append(productElement, termElement);
};

// Main function to style warranty line items
const styleWarrantyLineItem = (lineItemElement, index) => {
    const title = lineItemElement.querySelector(selectors.cartRowItemTitle);
    disablePointerEvents(title);
    
    const image = lineItemElement.querySelector(selectors.cartRowItemImage);
    if (image) {
        disablePointerEvents(image);
    } else {
        console.error(`EXTEND: Line item image element on line ${index} not found with selector ${selectors.cartRowItemImage}`);
    }
    
    // Remove original warranty metadata
    const originalMetadata = lineItemElement.querySelectorAll(selectors.warrantyOriginalMeta);
    originalMetadata.forEach(each => each.remove());
    
    // Disable quantity wrapper
    if (selectors.quantityWrapper?.length) {
        const quantityWrappers = lineItemElement.querySelectorAll(selectors.quantityWrapper);
        quantityWrappers.forEach(applyDisabledStyle);
    }
    
    // Add custom warranty metadata
    const metadataContainer = lineItemElement.querySelector(selectors.metadataContainer);
    if (metadataContainer) {
        const cartItem = cart.items[index];
        const { productData, termData } = getWarrantyMetadata(cartItem, cart.items);
        appendWarrantyMetadata(metadataContainer, productData, termData, type);
    }
};

// Pure function to check if title contains warranty text
const isWarrantyItem = (title) => {
    return title.toLowerCase().includes('extend protection');
};

// Function to process each line item
const processLineItem = (lineItemElement, index) => {
    const title = lineItemElement.querySelector(selectors.cartRowItemTitle);
    
    if (!title) {
        throw new Error('Line item title not found');
    }
    
    if (isWarrantyItem(title.innerText)) {
        styleWarrantyLineItem(lineItemElement, index);
    } else {
        createWarrantyOffer(lineItemElement, index);
    }
};

// Function to render or style all cart items
const renderOrStyleCart = () => {

    const cartContainer = document.querySelector(selectors.cartContainer);
    
    if (!cartContainer) {
        throw new Error(`Invalid cart container selector: ${selectors.cartContainer}`);
    }
    
    const lineItems = cartContainer.querySelectorAll(selectors.cartRowItem);
    lineItems.forEach(processLineItem);
    
    if (cartSPEnabled) {
        createShippingOffer();
    }
};

// Function to normalize cart items
const normalizeItems = (callback) => {
    if (SPEnabled) {
        ExtendShopify.updateExtendLineItems({
            balanceCart: cartBalancingEnabled,
            isSPSupported: cartSPEnabled,
            callback
        });
    } else {
        ExtendShopify.normalizeCart({
            balance: cartBalancingEnabled
        }, callback);
    }
};

// Function to refresh the cart display
const refreshCart = () => {
    if (type === 'side') {
        // Side cart refresh logic
    } else if (type === 'main') {
        publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'extend-cart'
        });
    } else {
        window.location.reload();
    }
};

// Function to verify cart configuration
const verifyCartOffers = () => {

    verifyExtend();

    const errors = [];
    
    if (!selectors) errors.push('Cart selectors are empty');
    if (!type) errors.push('Cart type not defined');

    if (errors.length > 0) {
        throw new Error(`Cannot initialize cart offers due to following errors:\n${errors.join('\n')}`);
    }
};

// Function to handle cart network requests
const handleCartRequest = (entry) => {
    const isValidRequestType = ['xmlhttprequest', 'fetch'].includes(entry.initiatorType);
    const isCartChangeRequest = /\/cart\//.test(entry.name);
    
    if (isValidRequestType && isCartChangeRequest) {
        setTimeout(initCartOffer, 250);
    }
};

// Function to setup performance observer
const setupCartObserver = () => {
    const cartObserver = new PerformanceObserver(list => {
        list.getEntries().forEach(handleCartRequest);
    });
    cartObserver.observe({ entryTypes: ['resource'] });
};

// Function to handle line item modifications
const handleLineItemModified = () => {
    if (type === 'main') {
        window.location.reload();
    }
};

// Function to initialize event listeners
const initEventListeners = () => {
    setupCartObserver();
    
    document.addEventListener('extend.lineItemModified', handleLineItemModified);
    
    init = true;
};

// Function to handle cart normalization callback
const handleNormalizeCallback = (err, data) => {
    cart = data?.cart;

    initializeCheckoutPlus(cart)

    if (err) {
        throw new Error(err.message);
    }
    
    if (data?.updates) {
        refreshCart();
    } else {
        renderOrStyleCart();
    }
};

// Main initialization function
export function initCartOffer() {
    verifyCartOffers();

    try {
        if (!window.location.pathname.includes('cart') && !sideCartOfferEnabled) {
            throw new Error('Cannot initialize cart offers - not a cart page');
        }
    } catch (err) {
        console.warn(err.message);
        return;
    }
    
    if (!init) {
        initEventListeners();
    }
    
    normalizeItems(handleNormalizeCallback);
}